import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, unref as _unref, Fragment as _Fragment, createElementVNode as _createElementVNode, renderList as _renderList, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "font-weight-regular opacity-90"
}
const _hoisted_2 = { class: "text-body-2" }
const _hoisted_3 = {
  key: 2,
  class: "pa-4",
  style: {"max-width":"800px","margin":"0 auto"}
}
const _hoisted_4 = {
  key: 0,
  class: "text-body-2 text-medium-emphasis mb-3"
}
const _hoisted_5 = { class: "d-flex flex-wrap ga-2 mb-5" }
const _hoisted_6 = { class: "d-flex align-center mb-5" }
const _hoisted_7 = { class: "text-body-2 font-weight-medium text-no-wrap" }
const _hoisted_8 = { class: "checklist-items" }
const _hoisted_9 = {
  class: "pa-3 pa-md-4",
  style: {"margin-left":"6px"}
}
const _hoisted_10 = { class: "d-flex align-center" }
const _hoisted_11 = {
  key: 2,
  class: "text-caption font-weight-bold"
}
const _hoisted_12 = { class: "text-body-1 font-weight-medium flex-grow-1" }
const _hoisted_13 = {
  key: 0,
  class: "d-none d-sm-flex ga-2 align-center"
}
const _hoisted_14 = {
  key: 1,
  class: "d-none d-sm-flex"
}
const _hoisted_15 = {
  key: 0,
  class: "d-flex d-sm-none ga-2 mt-3"
}
const _hoisted_16 = {
  key: 1,
  class: "d-flex d-sm-none mt-2"
}
const _hoisted_17 = {
  key: 0,
  class: "mt-3"
}
const _hoisted_18 = {
  key: 2,
  class: "mt-1 text-caption text-medium-emphasis"
}
const _hoisted_19 = { key: 0 }
const _hoisted_20 = { class: "font-weight-bold mb-1" }
const _hoisted_21 = { class: "text-body-2" }
const _hoisted_22 = { key: 0 }
const _hoisted_23 = {
  key: 2,
  class: "text-caption text-medium-emphasis mt-2 text-center"
}
const _hoisted_24 = {
  key: 3,
  class: "text-caption text-error mt-2 text-center"
}

import {computed, onBeforeUnmount, onMounted, reactive, ref} from 'vue'
import {supabase} from '@/api'
import {getCurrentUser, useGlobalSetting, useSecondPopup} from '@/lib/utils'
import {acquireChecklistLock, releaseChecklistLock} from '@/api/ckecklist'
import {useAuth} from '@/lib/useAuth'
import ChecklistAssignForm from '@/components/checklists/ChecklistAssignForm.vue'
import type {Checklist} from '@/types/Checklist'

interface ChecklistItem {
  label: string
  ok: boolean | null
  note: string | null
  showNote: boolean
  checked_by: string | null
  checked_at: string | null
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ChecklistFillForm',
  props: {
    checklistId: {}
  },
  emits: ["close", "completed", "saved"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const global = useGlobalSetting()
const saving = ref(false)
const fetchError = ref<string | null>(null)
const checklist = ref<Checklist | null>(null)
const items = reactive<ChecklistItem[]>([])
const hasUnsavedChanges = ref(false)
const hasLock = ref(false)
let lockRefreshInterval: ReturnType<typeof setInterval> | null = null

// Computed
const isCompleted = computed(() =>
    checklist.value?.state === 'BEEND' || checklist.value?.state === 'GESCHL'
)

const lockedByOther = computed(() =>
    checklist.value?.locked_by
    && checklist.value.locked_by !== getCurrentUser()
    && !hasLock.value
)

const canEdit = computed(() =>
    !isCompleted.value && !lockedByOther.value && hasLock.value
)

const isAdminOrMod = computed(() => ['admin', 'mod'].includes(useAuth().getRole()))

const checkedCount = computed(() => items.filter(i => i.ok !== null).length)
const progress = computed(() => items.length > 0 ? (checkedCount.value / items.length) * 100 : 0)
const allChecked = computed(() => items.length > 0 && checkedCount.value === items.length)
const hasNio = computed(() => items.some(i => i.ok === false))
const nioCount = computed(() => items.filter(i => i.ok === false).length)
const nioItems = computed(() => items.filter(i => i.ok === false))
const nioMissingNote = computed(() => items.some(i => i.ok === false && !i.note?.trim()))

// Helpers
function itemResult(item: ChecklistItem): string | null {
  if (item.ok === true) return 'io'
  if (item.ok === false) return 'nio'
  return null
}

function setResult(idx: number, value: string | null) {
  if (!value || !canEdit.value) return
  const item = items[idx]
  const newOk = value === 'io' ? true : value === 'nio' ? false : null

  if (item.ok === newOk) {
    item.ok = null
    item.checked_by = null
    item.checked_at = null
  } else {
    item.ok = newOk
    item.checked_by = getCurrentUser()
    item.checked_at = new Date().toISOString()
  }
  hasUnsavedChanges.value = true
}

function cardStyle(item: ChecklistItem) {
  if (item.ok === true) return {border: '2px solid #4CAF50', backgroundColor: '#E8F5E9'}
  if (item.ok === false) return {border: '2px solid #F44336', backgroundColor: '#FFEBEE'}
  return {border: '2px solid transparent'}
}

function formatDate(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null
  try {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  } catch {
    return dateStr
  }
}

function formatDateTime(dateStr: string | null): string | null {
  if (!dateStr) return null
  try {
    return new Date(dateStr).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    })
  } catch {
    return dateStr
  }
}

function getItemsPayload() {
  return items.map(({label, ok, note, checked_by, checked_at}) => ({
    label, ok, note: note || null, checked_by: checked_by || null, checked_at: checked_at || null
  }))
}

// Locking
async function tryAcquireLock() {
  if (isCompleted.value) return

  const success = await acquireChecklistLock(props.checklistId, getCurrentUser())
  hasLock.value = success

  if (success) {
    lockRefreshInterval = setInterval(async () => {
      await acquireChecklistLock(props.checklistId, getCurrentUser())
    }, 2 * 60 * 1000)
  }
}

async function releaseLock() {
  if (hasLock.value) {
    if (lockRefreshInterval) {
      clearInterval(lockRefreshInterval)
      lockRefreshInterval = null
    }
    await releaseChecklistLock(props.checklistId)
    hasLock.value = false
  }
}

async function onClose() {
  await releaseLock()
  emit('close')
}

// Data fetching
async function fetchChecklist() {
  global.loadingMessage = 'Checkliste wird geladen...'
  global.isLoading = true
  fetchError.value = null

  const {data, error} = await supabase
      .from('checklists')
      .select('*')
      .eq('id', props.checklistId)
      .single()

  if (error || !data) {
    fetchError.value = error?.message || 'Checkliste nicht gefunden'
    global.isLoading = false
    return
  }

  checklist.value = data as Checklist

  items.length = 0
  const dbItems = (data.items as any[]) || []
  for (const item of dbItems) {
    items.push({
      label: item.label || '',
      ok: item.ok ?? null,
      note: item.note ?? null,
      showNote: !!(item.note) || item.ok === false,
      checked_by: item.checked_by ?? null,
      checked_at: item.checked_at ?? null
    })
  }

  hasUnsavedChanges.value = false
  global.isLoading = false

  await tryAcquireLock()
}

// Save progress
async function saveProgress() {
  if (!checklist.value || !canEdit.value) return

  saving.value = true

  const {error} = await supabase
      .from('checklists')
      .update({
        items: getItemsPayload(),
        state: 'INBEARB'
      })
      .eq('id', checklist.value.id)

  if (error) {
    console.error('Save failed:', error)
  } else {
    hasUnsavedChanges.value = false
    if (checklist.value) {
      checklist.value.state = 'INBEARB'
      checklist.value.items = getItemsPayload()
    }
    emit('saved', checklist.value!)
  }

  saving.value = false
}

// Complete checklist
async function completeChecklist() {
  if (!checklist.value || !allChecked.value || !canEdit.value) return
  if (hasNio.value && nioMissingNote.value) return

  saving.value = true

  const now = new Date().toISOString()
  const {error} = await supabase
      .from('checklists')
      .update({
        items: getItemsPayload(),
        state: 'BEEND',
        completed_by: getCurrentUser(),
        completed_at: now,
        locked_by: null,
        locked_at: null
      })
      .eq('id', checklist.value.id)

  if (error) {
    console.error('Complete failed:', error)
  } else {
    if (checklist.value) {
      checklist.value.state = 'BEEND'
      checklist.value.completed_by = getCurrentUser()
      checklist.value.completed_at = now
      checklist.value.locked_by = null
      checklist.value.locked_at = null
      checklist.value.items = getItemsPayload()
    }
    hasUnsavedChanges.value = false
    hasLock.value = false
    if (lockRefreshInterval) {
      clearInterval(lockRefreshInterval)
      lockRefreshInterval = null
    }
    emit('completed', checklist.value!)
  }

  saving.value = false
}

function openEditDialog() {
  if (!checklist.value) return

  useSecondPopup().open(
      ChecklistAssignForm,
      {
        checklistId: checklist.value.id,
        checklistTitle: checklist.value.title,
        initialAssignees: checklist.value.assignees,
        initialAssetId: checklist.value.asset_id,
        initialOrderId: checklist.value.order_id,
        initialScheduledStart: checklist.value.scheduled_start,
        initialScheduledEnd: checklist.value.scheduled_end,
        onSaved: () => fetchChecklist(),
        onCancel: () => useSecondPopup().close()
      },
      {
        contentFilled: true,
        maxWidth: 600,
        persistent: false,
      }
  )
}

onMounted(fetchChecklist)

onBeforeUnmount(async () => {
  await releaseLock()
})

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_toolbar_title = _resolveComponent("v-toolbar-title")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_toolbar = _resolveComponent("v-toolbar")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_progress_linear = _resolveComponent("v-progress-linear")!
  const _component_v_btn_toggle = _resolveComponent("v-btn-toggle")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_expand_transition = _resolveComponent("v-expand-transition")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_sheet = _resolveComponent("v-sheet")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!

  return (_openBlock(), _createBlock(_component_v_card, {
    class: "d-flex flex-column",
    height: "100vh"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_sheet, { class: "flex-grow-1 overflow-auto" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_toolbar, {
            color: "primary",
            density: "compact",
            class: "px-4"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_icon, {
                icon: "mdi-clipboard-check-outline",
                class: "mr-2"
              }),
              _createVNode(_component_v_toolbar_title, { class: "text-subtitle-1 font-weight-medium" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(checklist.value?.title || 'Checkliste') + " ", 1),
                  (checklist.value?.asset_id)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_1, " - " + _toDisplayString(checklist.value.asset_id), 1))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }),
              (isAdminOrMod.value && !isCompleted.value)
                ? (_openBlock(), _createBlock(_component_v_btn, {
                    key: 0,
                    icon: "",
                    variant: "text",
                    onClick: openEditDialog
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, { icon: "mdi-pencil-outline" }),
                      _createVNode(_component_v_tooltip, {
                        activator: "parent",
                        location: "bottom"
                      }, {
                        default: _withCtx(() => _cache[0] || (_cache[0] = [
                          _createTextVNode("Zuweisung bearbeiten")
                        ])),
                        _: 1,
                        __: [0]
                      })
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              (_unref(useGlobalSetting)().isMobile)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    (canEdit.value && hasUnsavedChanges.value)
                      ? (_openBlock(), _createBlock(_component_v_btn, {
                          key: 0,
                          icon: "",
                          variant: "text",
                          loading: saving.value,
                          onClick: saveProgress
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_icon, { icon: "mdi-content-save-outline" }),
                            _createVNode(_component_v_tooltip, {
                              activator: "parent",
                              location: "bottom"
                            }, {
                              default: _withCtx(() => _cache[1] || (_cache[1] = [
                                _createTextVNode("Zwischenspeichern")
                              ])),
                              _: 1,
                              __: [1]
                            })
                          ]),
                          _: 1
                        }, 8, ["loading"]))
                      : _createCommentVNode("", true),
                    (canEdit.value)
                      ? (_openBlock(), _createBlock(_component_v_btn, {
                          key: 1,
                          icon: "",
                          variant: "text",
                          disabled: !allChecked.value || (hasNio.value && nioMissingNote.value) || saving.value,
                          loading: saving.value,
                          onClick: completeChecklist
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_icon, { icon: "mdi-check-all" }),
                            _createVNode(_component_v_tooltip, {
                              activator: "parent",
                              location: "bottom"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(hasNio.value ? 'Mit Mängeln abschließen' : 'Abschließen'), 1)
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }, 8, ["disabled", "loading"]))
                      : _createCommentVNode("", true),
                    _createVNode(_component_v_btn, {
                      icon: "",
                      variant: "text",
                      onClick: onClose
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, { icon: "mdi-close" }),
                        _createVNode(_component_v_tooltip, {
                          activator: "parent",
                          location: "bottom"
                        }, {
                          default: _withCtx(() => _cache[2] || (_cache[2] = [
                            _createTextVNode("Schließen")
                          ])),
                          _: 1,
                          __: [2]
                        })
                      ]),
                      _: 1
                    })
                  ], 64))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }),
          (lockedByOther.value)
            ? (_openBlock(), _createBlock(_component_v_alert, {
                key: 0,
                type: "warning",
                variant: "tonal",
                class: "ma-4",
                prominent: ""
              }, {
                default: _withCtx(() => [
                  _cache[5] || (_cache[5] = _createElementVNode("div", { class: "font-weight-bold" }, "Wird gerade bearbeitet", -1)),
                  _createElementVNode("div", _hoisted_2, [
                    _cache[3] || (_cache[3] = _createTextVNode(" Diese Checkliste wird gerade von ")),
                    _createElementVNode("strong", null, _toDisplayString(checklist.value?.locked_by), 1),
                    _cache[4] || (_cache[4] = _createTextVNode(" bearbeitet. Du kannst sie ansehen, aber nicht bearbeiten. "))
                  ])
                ]),
                _: 1,
                __: [5]
              }))
            : _createCommentVNode("", true),
          (fetchError.value)
            ? (_openBlock(), _createBlock(_component_v_alert, {
                key: 1,
                type: "error",
                variant: "tonal",
                class: "ma-4"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(fetchError.value), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (checklist.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                (checklist.value.description)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(checklist.value.description), 1))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_5, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(checklist.value.assignees, (user) => {
                    return (_openBlock(), _createBlock(_component_v_chip, {
                      key: user,
                      "prepend-icon": "mdi-account",
                      size: "small",
                      variant: "tonal"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(user), 1)
                      ]),
                      _: 2
                    }, 1024))
                  }), 128)),
                  _createVNode(_component_v_chip, {
                    "prepend-icon": "mdi-calendar",
                    size: "small",
                    variant: "tonal"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(formatDate(checklist.value.scheduled_start) || new Date().toLocaleDateString('de-DE')), 1)
                    ]),
                    _: 1
                  }),
                  (checklist.value.asset_id)
                    ? (_openBlock(), _createBlock(_component_v_chip, {
                        key: 0,
                        "prepend-icon": "mdi-factory",
                        size: "small",
                        variant: "tonal",
                        color: "primary"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(checklist.value.asset_id), 1)
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  (checklist.value.order_id)
                    ? (_openBlock(), _createBlock(_component_v_chip, {
                        key: 1,
                        "prepend-icon": "mdi-clipboard-text",
                        size: "small",
                        variant: "tonal"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(" Auftrag #" + _toDisplayString(checklist.value.order_id), 1)
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  _createVNode(_component_v_chip, {
                    "prepend-icon": "mdi-circle",
                    size: "small",
                    variant: "tonal",
                    color: hasNio.value ? 'error' : allChecked.value ? 'success' : 'default'
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(isCompleted.value ? 'Abgeschlossen' : allChecked.value ? (hasNio.value ? 'Mängel festgestellt' : 'Alles i.O.') : 'In Bearbeitung'), 1)
                    ]),
                    _: 1
                  }, 8, ["color"])
                ]),
                _createElementVNode("div", _hoisted_6, [
                  _createVNode(_component_v_progress_linear, {
                    "model-value": progress.value,
                    color: hasNio.value ? 'warning' : 'success',
                    height: "10",
                    rounded: "",
                    class: "me-3",
                    style: {"flex":"1"}
                  }, null, 8, ["model-value", "color"]),
                  _createElementVNode("span", _hoisted_7, _toDisplayString(checkedCount.value) + "/" + _toDisplayString(items.length), 1)
                ]),
                _createElementVNode("div", _hoisted_8, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(items, (item, idx) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: idx,
                      class: "mb-3"
                    }, [
                      _createVNode(_component_v_card, {
                        style: _normalizeStyle(cardStyle(item)),
                        elevation: item.ok !== null ? 0 : 1,
                        rounded: "lg",
                        class: "checklist-card"
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("div", {
                            class: "checklist-indicator",
                            style: _normalizeStyle({
                    backgroundColor: item.ok === true ? '#4CAF50' : item.ok === false ? '#F44336' : '#E0E0E0'
                  })
                          }, null, 4),
                          _createElementVNode("div", _hoisted_9, [
                            _createElementVNode("div", _hoisted_10, [
                              _createElementVNode("div", {
                                class: "checklist-number me-3",
                                style: _normalizeStyle({
                        backgroundColor: item.ok === true ? '#4CAF50' : item.ok === false ? '#F44336' : '#9E9E9E',
                        color: 'white'
                      })
                              }, [
                                (item.ok === true)
                                  ? (_openBlock(), _createBlock(_component_v_icon, {
                                      key: 0,
                                      icon: "mdi-check",
                                      size: "16"
                                    }))
                                  : (item.ok === false)
                                    ? (_openBlock(), _createBlock(_component_v_icon, {
                                        key: 1,
                                        icon: "mdi-close",
                                        size: "16"
                                      }))
                                    : (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(idx + 1), 1))
                              ], 4),
                              _createElementVNode("span", _hoisted_12, _toDisplayString(item.label), 1),
                              (canEdit.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                    _createVNode(_component_v_btn_toggle, {
                                      "model-value": itemResult(item),
                                      "onUpdate:modelValue": ($event: any) => (setResult(idx, $event)),
                                      divided: "",
                                      density: "compact",
                                      variant: "outlined",
                                      rounded: "pill",
                                      style: {"height":"36px"}
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_btn, {
                                          value: "io",
                                          color: item.ok === true ? 'success' : undefined,
                                          variant: item.ok === true ? 'flat' : 'outlined',
                                          class: "px-4"
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_v_icon, {
                                              icon: "mdi-check",
                                              size: "18",
                                              class: "me-1"
                                            }),
                                            _cache[6] || (_cache[6] = _createTextVNode(" i.O. "))
                                          ]),
                                          _: 2,
                                          __: [6]
                                        }, 1032, ["color", "variant"]),
                                        _createVNode(_component_v_btn, {
                                          value: "nio",
                                          color: item.ok === false ? 'error' : undefined,
                                          variant: item.ok === false ? 'flat' : 'outlined',
                                          class: "px-4"
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_v_icon, {
                                              icon: "mdi-close",
                                              size: "18",
                                              class: "me-1"
                                            }),
                                            _cache[7] || (_cache[7] = _createTextVNode(" n.i.O. "))
                                          ]),
                                          _: 2,
                                          __: [7]
                                        }, 1032, ["color", "variant"])
                                      ]),
                                      _: 2
                                    }, 1032, ["model-value", "onUpdate:modelValue"]),
                                    _createVNode(_component_v_btn, {
                                      icon: "",
                                      size: "small",
                                      variant: "text",
                                      onClick: ($event: any) => (item.showNote = !item.showNote)
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_icon, {
                                          icon: item.note ? 'mdi-comment-text' : 'mdi-comment-plus-outline',
                                          color: item.note ? 'primary' : undefined,
                                          size: "20"
                                        }, null, 8, ["icon", "color"])
                                      ]),
                                      _: 2
                                    }, 1032, ["onClick"])
                                  ]))
                                : _createCommentVNode("", true),
                              (!canEdit.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                                    _createVNode(_component_v_chip, {
                                      size: "small",
                                      color: item.ok === true ? 'success' : item.ok === false ? 'error' : 'grey',
                                      variant: "flat"
                                    }, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(item.ok === true ? 'i.O.' : item.ok === false ? 'n.i.O.' : 'Offen'), 1)
                                      ]),
                                      _: 2
                                    }, 1032, ["color"])
                                  ]))
                                : _createCommentVNode("", true)
                            ]),
                            (canEdit.value)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                                  _createVNode(_component_v_btn, {
                                    variant: item.ok === true ? 'flat' : 'outlined',
                                    color: item.ok === true ? 'success' : undefined,
                                    size: "default",
                                    class: "flex-grow-1",
                                    onClick: ($event: any) => (setResult(idx, 'io'))
                                  }, {
                                    default: _withCtx(() => [
                                      _createVNode(_component_v_icon, {
                                        icon: "mdi-check",
                                        class: "me-1"
                                      }),
                                      _cache[8] || (_cache[8] = _createTextVNode(" i.O. "))
                                    ]),
                                    _: 2,
                                    __: [8]
                                  }, 1032, ["variant", "color", "onClick"]),
                                  _createVNode(_component_v_btn, {
                                    variant: item.ok === false ? 'flat' : 'outlined',
                                    color: item.ok === false ? 'error' : undefined,
                                    size: "default",
                                    class: "flex-grow-1",
                                    onClick: ($event: any) => (setResult(idx, 'nio'))
                                  }, {
                                    default: _withCtx(() => [
                                      _createVNode(_component_v_icon, {
                                        icon: "mdi-close",
                                        class: "me-1"
                                      }),
                                      _cache[9] || (_cache[9] = _createTextVNode(" n.i.O. "))
                                    ]),
                                    _: 2,
                                    __: [9]
                                  }, 1032, ["variant", "color", "onClick"]),
                                  _createVNode(_component_v_btn, {
                                    icon: "",
                                    variant: "text",
                                    onClick: ($event: any) => (item.showNote = !item.showNote)
                                  }, {
                                    default: _withCtx(() => [
                                      _createVNode(_component_v_icon, {
                                        icon: item.note ? 'mdi-comment-text' : 'mdi-comment-plus-outline',
                                        color: item.note ? 'primary' : undefined
                                      }, null, 8, ["icon", "color"])
                                    ]),
                                    _: 2
                                  }, 1032, ["onClick"])
                                ]))
                              : _createCommentVNode("", true),
                            (!canEdit.value)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                                  _createVNode(_component_v_chip, {
                                    size: "small",
                                    color: item.ok === true ? 'success' : item.ok === false ? 'error' : 'grey',
                                    variant: "flat"
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(item.ok === true ? 'i.O.' : item.ok === false ? 'n.i.O.' : 'Offen'), 1)
                                    ]),
                                    _: 2
                                  }, 1032, ["color"])
                                ]))
                              : _createCommentVNode("", true),
                            _createVNode(_component_v_expand_transition, null, {
                              default: _withCtx(() => [
                                (item.showNote || item.ok === false)
                                  ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                                      _createVNode(_component_v_textarea, {
                                        modelValue: item.note,
                                        "onUpdate:modelValue": ($event: any) => ((item.note) = $event),
                                        label: item.ok === false ? 'Bemerkung (Pflicht bei n.i.O.)' : 'Bemerkung (optional)',
                                        color: item.ok === false ? 'error' : 'primary',
                                        readonly: !canEdit.value,
                                        variant: _unref(useGlobalSetting)().globalTextVariant,
                                        density: "compact",
                                        rows: "2",
                                        "auto-grow": "",
                                        "hide-details": "",
                                        rounded: "lg"
                                      }, null, 8, ["modelValue", "onUpdate:modelValue", "label", "color", "readonly", "variant"])
                                    ]))
                                  : _createCommentVNode("", true)
                              ]),
                              _: 2
                            }, 1024),
                            (item.checked_by)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                                  _createVNode(_component_v_icon, {
                                    icon: "mdi-account-edit",
                                    size: "12",
                                    class: "me-1"
                                  }),
                                  _createTextVNode(" " + _toDisplayString(item.checked_by) + " ", 1),
                                  (item.checked_at)
                                    ? (_openBlock(), _createElementBlock("span", _hoisted_19, " · " + _toDisplayString(formatDateTime(item.checked_at)), 1))
                                    : _createCommentVNode("", true)
                                ]))
                              : _createCommentVNode("", true)
                          ])
                        ]),
                        _: 2
                      }, 1032, ["style", "elevation"])
                    ]))
                  }), 128))
                ]),
                _createVNode(_component_v_expand_transition, null, {
                  default: _withCtx(() => [
                    (hasNio.value)
                      ? (_openBlock(), _createBlock(_component_v_alert, {
                          key: 0,
                          type: "warning",
                          variant: "tonal",
                          class: "mb-4",
                          prominent: ""
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_20, _toDisplayString(nioCount.value) + " Mängel festgestellt", 1),
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(nioItems.value, (item) => {
                              return (_openBlock(), _createElementBlock("div", {
                                key: item.label,
                                class: "text-body-2"
                              }, " • " + _toDisplayString(item.label) + _toDisplayString(item.note ? ': ' + item.note : ''), 1))
                            }), 128))
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }),
                (isCompleted.value)
                  ? (_openBlock(), _createBlock(_component_v_alert, {
                      key: 1,
                      type: "success",
                      variant: "tonal",
                      class: "mb-4",
                      prominent: ""
                    }, {
                      default: _withCtx(() => [
                        _cache[10] || (_cache[10] = _createElementVNode("div", { class: "font-weight-bold" }, "Abgeschlossen", -1)),
                        _createElementVNode("div", _hoisted_21, [
                          _createTextVNode(" von " + _toDisplayString(checklist.value.completed_by) + " ", 1),
                          (checklist.value.completed_at)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_22, " am " + _toDisplayString(formatDate(checklist.value.completed_at)), 1))
                            : _createCommentVNode("", true)
                        ])
                      ]),
                      _: 1,
                      __: [10]
                    }))
                  : _createCommentVNode("", true),
                (canEdit.value && !allChecked.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_23, " Alle Prüfpunkte müssen bewertet werden "))
                  : (canEdit.value && nioMissingNote.value)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_24, " Alle n.i.O. Punkte benötigen eine Bemerkung "))
                    : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      (!_unref(useGlobalSetting)().isMobile)
        ? (_openBlock(), _createBlock(_component_v_divider, { key: 0 }))
        : _createCommentVNode("", true),
      (!_unref(useGlobalSetting)().isMobile)
        ? (_openBlock(), _createBlock(_component_v_card_actions, {
            key: 1,
            class: "bg-surface-light"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_btn, {
                variant: "plain",
                onClick: onClose
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, {
                    color: "red",
                    icon: "mdi-cancel",
                    start: ""
                  }),
                  _cache[11] || (_cache[11] = _createTextVNode(" Schließen "))
                ]),
                _: 1,
                __: [11]
              }),
              _createVNode(_component_v_spacer),
              (canEdit.value && hasUnsavedChanges.value)
                ? (_openBlock(), _createBlock(_component_v_btn, {
                    key: 0,
                    variant: "plain",
                    loading: saving.value,
                    onClick: saveProgress
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, {
                        color: "primary",
                        icon: "mdi-content-save-outline",
                        start: ""
                      }),
                      _cache[12] || (_cache[12] = _createTextVNode(" Zwischenspeichern "))
                    ]),
                    _: 1,
                    __: [12]
                  }, 8, ["loading"]))
                : _createCommentVNode("", true),
              (canEdit.value)
                ? (_openBlock(), _createBlock(_component_v_btn, {
                    key: 1,
                    variant: "plain",
                    disabled: !allChecked.value || (hasNio.value && nioMissingNote.value) || saving.value,
                    loading: saving.value,
                    onClick: completeChecklist
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, {
                        color: hasNio.value ? 'warning' : 'success',
                        icon: "mdi-check-all",
                        start: ""
                      }, null, 8, ["color"]),
                      _createTextVNode(" " + _toDisplayString(hasNio.value ? 'Mit Mängeln abschließen' : 'Abschließen'), 1)
                    ]),
                    _: 1
                  }, 8, ["disabled", "loading"]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})